import React from "react";
import { Heart, Plus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { getImageUrl, formatCurrency } from "../core/api.jsx";
import { activeVariants, catalogPrice } from "./variantUtils";
import { useCart } from "../contexts/CartContext";
import { useWishlist } from "../contexts/WishlistContext";
import { useToast } from "../contexts/ToastContext";

export default function ProductCard({ product, compact = false }) {
  const { addToCart } = useCart();
  const { toggleWishlist, wishlistIds } = useWishlist();
  const { notify } = useToast();
  const navigate = useNavigate();
  const id = String(product?._id || product?.id || "");
  const offer = catalogPrice(product);
  const price = offer.price;
  const oldPrice = offer.compareAt;
  const hasVariants = activeVariants(product).length > 0;
  const outOfStock = offer.stock < 1;
  const discount = oldPrice > price && oldPrice ? Math.round((1 - price / oldPrice) * 100) : 0;
  const image = getImageUrl(product?.images?.[0]);
  const description = String(product?.shortDescription || product?.description || "").trim();
  const href = product?.slug && product?.productCode ? `/product/${product.slug}/${product.productCode}` : "/shop";

  const handleAdd = async (event) => {
    event.preventDefault();
    if (outOfStock) return;
    if (hasVariants) {
      navigate(href);
      notify("Choose a variant to continue.");
      return;
    }
    try {
      await addToCart(product);
      notify("Added to your bag.");
    } catch (error) {
      notify(error.message, "error");
    }
  };

  const handleWishlist = async (event) => {
    event.preventDefault();
    try {
      await toggleWishlist(product);
      notify(wishlistIds.includes(id) ? "Removed from saved items." : "Saved for later.");
    } catch (error) {
      notify(error.message, "error");
    }
  };

  return (
    <article className={`product-card${compact ? " product-card--compact" : ""}`}>
      <div className="product-card__media">
        <Link to={href} aria-label={`View ${product?.name}`}>
          {image ? <img src={image} alt={product?.name || "Sugam Store product"} loading="lazy" /> : <div className="image-placeholder">Sugam<br />Store</div>}
        </Link>
        {discount > 0 && <span className="product-card__badge">{discount}% off</span>}
        <button className={`icon-button product-card__heart${wishlistIds.includes(id) ? " is-active" : ""}`} aria-label="Save item" onClick={handleWishlist}>
          <Heart size={17} fill={wishlistIds.includes(id) ? "currentColor" : "none"} />
        </button>
      </div>
      <div className="product-card__body">
        <div className="product-card__taxonomy">
          <span className="product-card__category">{product?.category || "Everyday favourite"}</span>
          {product?.subCategory && (
            <>
              <span className="product-card__taxonomy-dot" aria-hidden="true">·</span>
              <span className="product-card__subcategory">{product.subCategory}</span>
            </>
          )}
        </div>
        <h3><Link to={href}>{product?.name || "Product"}</Link></h3>
        {description && <p className="product-card__description">{description}</p>}
        <div className="product-card__rating">
          <span>★</span>
          {Number(product?.ratings) > 0 ? (
            <>
              <strong>{Number(product.ratings).toFixed(1)}</strong>
              <small>({product.numOfReviews || 0})</small>
            </>
          ) : (
            <small>No reviews yet</small>
          )}
        </div>
        <div className="product-card__footer">
          <div className="product-card__price">
            <strong>{offer.from ? "From " : ""}{formatCurrency(price)}</strong>
            {oldPrice > price && <del>{formatCurrency(oldPrice)}</del>}
          </div>
          <button className="product-card__add" aria-label={`Add ${product?.name} to bag`} onClick={handleAdd} disabled={outOfStock}>
            <Plus size={16} /> <span>{outOfStock ? "Sold out" : hasVariants ? "Choose" : "Buy"}</span>
          </button>
        </div>
      </div>
    </article>
  );
}
